<script setup lang="ts">
import { useRoute, LoadingView } from '@hfc/ui-core';
import { UUID } from '@hfc/common';
import { computed, onMounted, ref } from 'vue';
import { ChallengeType } from '@/application/shared/domain/ChallengeQuestionSettings';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { useHandleApiError } from '@/shared/infrastructure/composition/useHandleApiError';
import { CCAccountDataResponse } from '@/shared/domain/CCAccountDataResponse';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import FeedbackModalCe from '@/shared/infrastructure/views/FeedbackModal.ce.vue';
import { useAppStore } from '@/shared/infrastructure/composition/useAppStore';

const { appStore, appState } = useAppStore();

const route = useRoute();

const { handleSfApiError } = useHandleApiError();

const uuid = route.params.uuid as UUID;

const CCAccountData = ref<CCAccountDataResponse | null>(null);
const validCCAccountData = computed(() => !appState.value.loading && CCAccountData.value && CCAccountData.value.access_code);

const getCCAccountData = async (): Promise<void> => {
  const response = await appStore.getCCAccountData(APPLICATION_TYPES.HEALTH, ChallengeType.APPLICATION, uuid);

  if (response && response.error) {
    return handleSfApiError(response.error, true);
  }

  if (response && response.data) {
    CCAccountData.value = response.data;
    localStorage.removeItem('hfc_enrollment_token');
  }
};

onMounted(() => {
  getCCAccountData();
});
</script>
<template>
  <div class="pt-10" v-if="validCCAccountData">
    <h1 class="text-center">Your Covered California Login Information</h1>
  </div>
  <BaseSection class="my-10">
    <div v-if="appState.loading" class="flex w-full h-96">
      <LoadingView class="animate-pulse" />
    </div>
    <div v-else-if="validCCAccountData" class="flex flex-col w-full text-lg px-10 py-6 space-y-6">
      <div class="flex flex-row space-x-10">
        <p>Hi <span class="font-bold">{{ CCAccountData?.first_name }} {{ CCAccountData?.last_name }}</span>,</p>
      </div>

      <div>
        <p>To access your Covered California account, make sure that you do not have any other Covered California web pages opened and you are NOT already logged in to Covered California. If you have multiple accounts at Covered California, you may have to call <a href="tel:1-800-300-1506">1-800-300-1506</a> to get access.</p>
      </div>
      <div class="space-y-4">
        <span class="font-semibold">SETUP</span>
        <p>Complete the following steps:</p>
        <div class="flex flex-col rounded-md bg-slate-100 border border-slate-150 p-4 space-y-3">
          <p>- Go to <a href="https://hfca.cc/access" target="_blank">hfca.cc/access</a></p>
          <p>- Enter the following "Access Code": {{ CCAccountData?.access_code }}</p>
          <p>- Follow the prompts</p>
        </div>
        <p>Case Number: {{ CCAccountData?.case_number }}</p>
      </div>

      <div class="space-y-4">
        <span class="font-semibold">HOW TO USE YOUR ACCOUNT</span>
        <p>Use your Covered California account to view notices and letters, upload requested documents, change plans (Nov-Jan), and review your application and plan details.
          Be cautious, but you can also update contact information, household size, or income.* <span class="font-semibold">(see warning below)</span></p>
      </div>

      <div class="space-y-4">
        <span class="font-semibold">WE'RE HERE TO HELP</span>
        <p>If you receive a notice you do not understand or think something needs to be updated, please contact us on the priority phone number, <a href="tel:877-250-6260">877-250-6260</a>, and we will take care of it with you.</p>
        <p>Our goal is to give you transparency and peace of mind while making sure your coverage and financial assistance remain accurate and uninterrupted. We are always happy to help.
        Take care!</p>
      </div>

      <div class="space-y-4">
        <span class="font-semibold">*Warning! It's easy to mess things up.</span>
        <p>
          Be aware that changes you make, even small ones, can lead to unintended issues such as loss of subsidies, incorrect eligibility determinations, or placement into Medi-Cal. These situations can be stressful and time consuming to fix. Normally, we recommend calling the Priority Phone number <a href="tel:877-250-6260">877-250-6260</a> to make these kinds of changes. Or, click
          <FeedbackModalCe :application-type="APPLICATION_TYPES.HEALTH" :uuid="uuid" display-mode="link">
            <span class="text-blue-400 cursor-pointer hover:text-blue-600">here</span>
          </FeedbackModalCe>
          to message us.
        </p>
      </div>
    </div>
    <div v-else-if="!validCCAccountData" class="flex flex-col items-center justify-center w-full text-2xl text-slate-600 h-96">
      <div>Sorry, your Covered California Account doesn't have an access code yet.</div>
    </div>
  </BaseSection>
</template>
